import React, { useState } from 'react'
import type { TCustomEndpointAccessCheckDto } from '@homepal/api-contract'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import TextField from 'components_new/atoms/TextField'

import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { TCustomEndpointLabels } from 'utils/sources'
import { useEntitySets } from 'redux/hooks/CustomEndpoints'

import { checkAccess } from 'redux/api/DataPlatform/connections/CustomEndpoints'

interface AddEndpointDialogProps {
  activeSourceId: string
  /** From the source itself: whether check-access is supported for it. */
  canCheckAccess: boolean
  labels: TCustomEndpointLabels
  onClose: () => void
  onSubmit: (
    body: { table_name: string; technical_name: string },
    callback: (err?: boolean) => void
  ) => void
  /**
   * Opens the paste-a-list flow instead. Only offered for sources where the
   * endpoint is typed rather than picked, since a pasted list has nothing
   * to pick from.
   */
  onSwitchToMany: () => void
  open: boolean
}

type TAccessCheckState = {
  checking: boolean
  /** Null until a check has finished for the current name. */
  result: TCustomEndpointAccessCheckDto | null
}

const INITIAL_ACCESS_CHECK: TAccessCheckState = {
  checking: false,
  result: null
}

const AddEndpointDialog = (props: AddEndpointDialogProps) => {
  const {
    activeSourceId,
    canCheckAccess,
    labels,
    onClose,
    onSubmit,
    onSwitchToMany,
    open
  } = props

  const entitySets = useEntitySets()

  const [technicalName, setTechnicalName] = useState('')
  const [corresponding, setCorresponding] = useState('')
  const [failed, setFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [accessCheck, setAccessCheck] =
    useState<TAccessCheckState>(INITIAL_ACCESS_CHECK)

  const handleClose = () => {
    setTechnicalName('')
    setCorresponding('')
    setFailed(false)
    setSaving(false)
    setAccessCheck(INITIAL_ACCESS_CHECK)
    onClose()
  }

  // A check only speaks for the name it was run against.
  const handleChangeTechnicalName = (value: string) => {
    setTechnicalName(value)
    setAccessCheck(INITIAL_ACCESS_CHECK)
  }

  const handleCheckAccess = () => {
    setAccessCheck({ checking: true, result: null })

    checkAccess({
      active_source_id: activeSourceId,
      technical_name: technicalName.trim()
    })
      .then((response) =>
        setAccessCheck({ checking: false, result: response.data.data })
      )
      .catch(() =>
        setAccessCheck({
          checking: false,
          result: {
            accessible: false,
            error: 'Åtkomsten kunde inte kontrolleras just nu.'
          }
        })
      )
  }

  /**
   * The backend rejects a name that is already taken, or a table name that
   * collides with an existing source table. Keep the dialog open in that case
   * so what was typed is still there to correct.
   */
  const handleSubmit = () => {
    setFailed(false)
    setSaving(true)

    onSubmit(
      {
        table_name: labels.skipCorresponding ? technicalName : corresponding,
        technical_name: technicalName
      },
      (err) => {
        setSaving(false)

        if (err) {
          setFailed(true)
        } else {
          handleClose()
        }
      }
    )
  }

  return (
    <SimpleDialog
      actions={
        <>
          <Button onClick={handleClose} variant="text">
            Avbryt
          </Button>
          <Button
            disabled={
              !technicalName || (!corresponding && !labels.skipCorresponding)
            }
            loading={saving}
            onClick={handleSubmit}
            variant="contained"
          >
            Lägg till
          </Button>
        </>
      }
      onClose={handleClose}
      open={open}
      title={`Lägg till ${labels.singular.toLowerCase()}`}
    >
      {labels.simpleDefinition ? (
        <TextField
          fullWidth={true}
          label={labels.singular}
          onChange={(event) => handleChangeTechnicalName(event.target.value)}
          value={technicalName}
        />
      ) : (
        <TextField
          fullWidth={true}
          helperText={
            entitySets.length === 0
              ? 'Inga entiteter kunde hämtas från källan.'
              : undefined
          }
          label={labels.singular}
          onChange={(event) => handleChangeTechnicalName(event.target.value)}
          select={entitySets.length > 0}
          value={technicalName}
        >
          {entitySets.map((entitySet) => (
            <MenuItem key={entitySet.url} value={entitySet.url}>
              {entitySet.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      {canCheckAccess ? (
        <>
          <Box>
            <Button
              disabled={!technicalName.trim()}
              loading={accessCheck.checking}
              onClick={handleCheckAccess}
            >
              Kontrollera åtkomst
            </Button>
          </Box>

          {accessCheck.result ? (
            <Alert
              severity={accessCheck.result.accessible ? 'success' : 'error'}
            >
              {accessCheck.result.accessible
                ? `${labels.singular} går att läsa.`
                : `Kunde inte läsa ${labels.singular.toLowerCase()}: ${accessCheck.result.error}`}
            </Alert>
          ) : null}
        </>
      ) : null}

      {labels.skipCorresponding ? null : (
        <TextField
          fullWidth={true}
          label={labels.corresponding}
          onChange={(event) => setCorresponding(event.target.value)}
          value={corresponding}
        />
      )}

      {labels.simpleDefinition ? (
        <Box>
          <Button
            onClick={() => {
              handleClose()
              onSwitchToMany()
            }}
            variant="text"
          >
            Lägg till flera på en gång
          </Button>
        </Box>
      ) : null}

      {failed ? (
        <Alert severity="error">
          {`Kunde inte läggas till. Kontrollera att ${labels.singular.toLowerCase()} och ${labels.corresponding.toLowerCase()} inte redan används.`}
        </Alert>
      ) : null}
    </SimpleDialog>
  )
}

export default AddEndpointDialog
